// Swipe Interface Styles
// Styled components for card-based dating interface

import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../theme';

// Main Container
export const SwipeInterfaceContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;
  touch-action: manipulation; /* Prevent zoom on double tap */
  -webkit-touch-callout: none; /* Disable callout on iOS */
  -webkit-user-select: none; /* Disable text selection on iOS */
  user-select: none; /* Disable text selection */

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 100vh;
    min-height: 100dvh; /* Dynamic viewport height for mobile */
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    min-height: 100vh;
    min-height: 100dvh; /* Dynamic viewport height for mobile */
  }

  @media (max-width: 360px) {
    padding: 0.25rem;
  }
`;

// Card Stack Container
export const SwipeCardStackContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  height: 640px;
  perspective: 1000px;
  margin: 4rem auto 2rem auto;

  @media (max-width: 768px) {
    max-width: 95vw;
    height: 80vh;
    height: 80dvh; /* Dynamic viewport height */
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    max-width: 95vw;
    height: 80vh;
    height: 80dvh; /* Dynamic viewport height */
    margin: 0 auto;
  }

  @media (max-width: 360px) {
    max-width: 95vw;
    height: 80vh;
    height: 80dvh; /* Dynamic viewport height */
    margin: 0 auto;
  }
`;

// Individual Card
export const SwipeCardContainer = styled.div<{ 
  $zIndex: number; 
  $isTopCard: boolean; 
  $isAnimating: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 20px;
  box-shadow: ${props => props.$isTopCard 
    ? '0 20px 40px rgba(0, 0, 0, 0.3)' 
    : '0 10px 20px rgba(0, 0, 0, 0.2)'};
  cursor: ${props => props.$isTopCard ? 'grab' : 'default'};
  transition: ${props => props.$isAnimating 
    ? 'none' 
    : 'box-shadow 0.3s ease'};
  z-index: ${props => props.$zIndex};
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:active {
    cursor: ${props => props.$isTopCard ? 'grabbing' : 'default'};
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    box-shadow: ${props => props.$isTopCard 
      ? '0 15px 30px rgba(0, 0, 0, 0.25)' 
      : '0 8px 16px rgba(0, 0, 0, 0.15)'};
  }

  @media (max-width: 480px) {
    border-radius: 12px;
    box-shadow: ${props => props.$isTopCard 
      ? '0 12px 24px rgba(0, 0, 0, 0.2)' 
      : '0 6px 12px rgba(0, 0, 0, 0.1)'};
  }

  @media (max-width: 360px) {
    border-radius: 8px;
    box-shadow: ${props => props.$isTopCard 
      ? '0 10px 20px rgba(0, 0, 0, 0.15)' 
      : '0 5px 10px rgba(0, 0, 0, 0.1)'};
  }
`;

// Card Face (simplified - using conditional rendering instead of CSS transforms)
export const CardFace = styled.div<{ $isFlipped: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
  background: white;
  pointer-events: all;
  z-index: 1;
  isolation: isolate;
`;

// Card Teaser (Front Face)
export const CardTeaserContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

// Playing Card Suit Decorations (on card container)
export const CardSuitCorner = styled.div<{ $suit: string; $color: string; $position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }>`
  position: absolute;
  ${props => {
    switch (props.$position) {
      case 'top-left':
        return 'top: 16px; left: 16px;';
      case 'top-right':
        return 'top: 16px; right: 16px; transform: rotate(180deg);';
      case 'bottom-left':
        return 'bottom: 16px; left: 16px; transform: rotate(180deg);';
      case 'bottom-right':
        return 'bottom: 16px; right: 16px;';
    }
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10; /* Above card faces */
  pointer-events: none;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    padding: 7px;
    ${props => {
      switch (props.$position) {
        case 'top-left':
          return 'top: 12px; left: 12px;';
        case 'top-right':
          return 'top: 12px; right: 12px;';
        case 'bottom-left':
          return 'bottom: 12px; left: 12px;';
        case 'bottom-right':
          return 'bottom: 12px; right: 12px;';
      }
    }}
  }

  @media (max-width: 480px) {
    padding: 6px;
    ${props => {
      switch (props.$position) {
        case 'top-left':
          return 'top: 10px; left: 10px;';
        case 'top-right':
          return 'top: 10px; right: 10px;';
        case 'bottom-left':
          return 'bottom: 10px; left: 10px;';
        case 'bottom-right':
          return 'bottom: 10px; right: 10px;';
      }
    }}
  }
`;

export const CardSuitSymbol = styled.div<{ $color: string }>`
  font-size: 32px;
  line-height: 1;
  color: ${props => props.$color};
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

export const CardPhoto = styled.div<{ $photoUrl: string }>`
  width: 100%;
  height: 70%;
  background-image: url(${props => props.$photoUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  }
`;

export const CardVideo = styled.video`
  width: 100%;
  height: 70%;
  object-fit: cover;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    pointer-events: none;
  }
`;

export const CardTeaserInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  color: white;
  z-index: 2;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 50%, transparent 100%);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }

  @media (max-width: 360px) {
    padding: 0.75rem;
  }
`;

export const CardName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 1.375rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    margin: 0 0 0.375rem 0;
  }

  @media (max-width: 360px) {
    font-size: 1.125rem;
    margin: 0 0 0.25rem 0;
  }
`;

export const CardAge = styled.span`
  font-size: 1.125rem;
  font-weight: 500;
  opacity: 0.9;
  margin-right: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-right: 0.375rem;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
    margin-right: 0.25rem;
  }
`;

export const CardLocation = styled.span`
  font-size: 1rem;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }

  @media (max-width: 360px) {
    font-size: 0.625rem;
  }
`;

export const CardHint = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  margin-top: 0.5rem;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-top: 0.375rem;
  }

  @media (max-width: 480px) {
    font-size: 0.625rem;
    margin-top: 0.25rem;
  }

  @media (max-width: 360px) {
    font-size: 0.5rem;
    margin-top: 0.125rem;
  }
`;

export const CardDistance = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    border-radius: 12px;
  }

  @media (max-width: 360px) {
    top: 0.375rem;
    right: 0.375rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.5rem;
    border-radius: 8px;
  }
`;

export const FlipHint = styled.div`
  position: absolute;
  bottom: 6rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  z-index: 5;
  pointer-events: none;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    bottom: 5.5rem;
    font-size: 0.6875rem;
    padding: 0.4375rem 0.875rem;
  }

  @media (max-width: 480px) {
    bottom: 5rem;
    font-size: 0.625rem;
    padding: 0.375rem 0.75rem;
    gap: 0.375rem;
  }

  @media (max-width: 360px) {
    bottom: 4.5rem;
    font-size: 0.5625rem;
    padding: 0.3125rem 0.625rem;
  }
`;

// Flip Icon (subtle)
export const FlipIcon = styled.span`
  font-size: 0.875rem;
  display: inline-block;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }

  @media (max-width: 360px) {
    font-size: 0.6875rem;
  }
`;

// Corner Fold Indicator (subtle page curl effect)
export const CornerFoldIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 70px; /* Positioned to not overlap suit symbols */
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 45px 45px 0;
  border-color: transparent rgba(255, 255, 255, 0.3) transparent transparent;
  z-index: 8;
  pointer-events: none;
  filter: drop-shadow(-2px 2px 4px rgba(0, 0, 0, 0.3));
  
  &::after {
    content: '👆';
    position: absolute;
    top: 8px;
    right: -40px;
    font-size: 16px;
    animation: pointFinger 2s ease-in-out infinite;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
  }

  @keyframes pointFinger {
    0%, 100% {
      transform: translate(0, 0) rotate(-25deg);
      opacity: 0.7;
    }
    50% {
      transform: translate(-3px, -3px) rotate(-25deg);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    right: 65px;
    border-width: 0 40px 40px 0;
    
    &::after {
      font-size: 14px;
      right: -36px;
      top: 7px;
    }
  }

  @media (max-width: 480px) {
    right: 60px;
    border-width: 0 35px 35px 0;
    
    &::after {
      font-size: 12px;
      right: -32px;
      top: 6px;
    }
  }

  @media (max-width: 360px) {
    right: 55px;
    border-width: 0 30px 30px 0;
    
    &::after {
      font-size: 11px;
      right: -28px;
      top: 5px;
    }
  }
`;

// Card Revealed (Back Face)
export const CardRevealedContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); /* Temporary debug background */
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }

  @media (max-width: 360px) {
    padding: 0.75rem;
  }
`;

export const CardRevealedHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

export const CardRevealedPhoto = styled.div<{ $photoUrl: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-image: url(${props => props.$photoUrl});
  background-size: cover;
  background-position: center;
  margin-right: 1rem;
  border: 3px solid #667eea;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    margin-right: 0.75rem;
    border-width: 2px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    margin-right: 0.5rem;
    border-width: 2px;
  }

  @media (max-width: 360px) {
    width: 32px;
    height: 32px;
    margin-right: 0.375rem;
    border-width: 1px;
  }
`;

export const CardRevealedInfo = styled.div`
  flex: 1;
`;

export const CardRevealedName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #333;
`;

export const CardRevealedAge = styled.span`
  font-size: 1rem;
  color: #666;
  margin-right: 0.5rem;
`;

export const CardRevealedLocation = styled.span`
  font-size: 0.875rem;
  color: #888;
`;

export const CardBio = styled.p`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #555;
  margin: 0 0 1rem 0;
`;

export const CardInterests = styled.div`
  margin-bottom: 1rem;
`;

export const CardInterestsTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #333;
`;

export const CardInterestsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const CardInterestTag = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
`;

export const CardCompatibility = styled.div`
  margin-bottom: 1rem;
`;

export const CardCompatibilityTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #333;
`;

export const CardCompatibilityBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
`;

export const CardCompatibilityFill = styled.div<{ $percentage: number }>`
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  width: ${props => props.$percentage}%;
  transition: width 0.3s ease;
`;

export const CardCompatibilityScore = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

export const CardSocialProof = styled.div`
  margin-bottom: 1rem;
`;

export const CardSocialProofItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #666;
`;

export const CardSocialProofIcon = styled.span`
  margin-right: 0.5rem;
  font-size: 1rem;
`;

// Action Buttons
export const CardActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
    padding-top: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding-top: 0.5rem;
    flex-wrap: wrap;
  }

  @media (max-width: 360px) {
    gap: 0.375rem;
    padding-top: 0.375rem;
  }
`;

export const CardActionButton = styled.button<{ 
  $variant: 'pass' | 'like' | 'superlike' | 'save';
  $disabled?: boolean;
}>`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  touch-action: manipulation;
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  position: relative;
  z-index: 10;

  ${props => {
    switch (props.$variant) {
      case 'pass':
        return `
          background: #f5f5f5;
          color: #666;
          &:hover:not(:disabled) {
            background: #e0e0e0;
            transform: translateY(-1px);
          }
        `;
      case 'like':
        return `
          background: linear-gradient(135deg, #ff6b6b 0%, #ee4d4d 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(255, 107, 107, 0.3);
          }
        `;
      case 'superlike':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'save':
        return `
          background: linear-gradient(135deg, #26de81 0%, #20bf6b 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(38, 222, 129, 0.3);
          }
        `;
      default:
        return '';
    }
  }}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 0.875rem;
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 360px) {
    padding: 0.375rem 0.5rem;
    font-size: 0.625rem;
  }
`;

// Swipe Controls
export const SwipeControlsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }
`;

export const SwipeControlButton = styled.button<{ 
  $variant: 'pass' | 'like' | 'superlike' | 'save' | 'undo';
  $disabled?: boolean;
}>`
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 50%;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  ${props => {
    switch (props.$variant) {
      case 'pass':
        return `
          background: #f5f5f5;
          color: #666;
          &:hover:not(:disabled) {
            background: #e0e0e0;
            transform: scale(1.1);
          }
        `;
      case 'like':
        return `
          background: linear-gradient(135deg, #ff6b6b 0%, #ee4d4d 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: scale(1.1);
            box-shadow: 0 6px 12px rgba(255, 107, 107, 0.3);
          }
        `;
      case 'superlike':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: scale(1.1);
            box-shadow: 0 6px 12px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'save':
        return `
          background: linear-gradient(135deg, #26de81 0%, #20bf6b 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: scale(1.1);
            box-shadow: 0 6px 12px rgba(38, 222, 129, 0.3);
          }
        `;
      case 'undo':
        return `
          background: #f5f5f5;
          color: #666;
          &:hover:not(:disabled) {
            background: #e0e0e0;
            transform: scale(1.1);
          }
        `;
      default:
        return '';
    }
  }}

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    font-size: 1.375rem;
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }

  @media (max-width: 360px) {
    width: 44px;
    height: 44px;
    font-size: 1.125rem;
  }
`;

// Loading and Error States
export const SwipeLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: white;
`;

export const SwipeLoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const SwipeLoadingText = styled.p`
  font-size: 1rem;
  margin: 0;
  opacity: 0.8;
`;

export const SwipeErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: white;
  text-align: center;
`;

export const SwipeErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

export const SwipeErrorText = styled.p`
  font-size: 1rem;
  margin: 0 0 1rem 0;
  opacity: 0.8;
`;

export const SwipeErrorButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Empty State
export const SwipeEmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: white;
  text-align: center;
`;

export const SwipeEmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

export const SwipeEmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

export const SwipeEmptyText = styled.p`
  font-size: 1rem;
  margin: 0 0 1.5rem 0;
  opacity: 0.8;
`;

export const SwipeEmptyButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
  }
`;

// Analytics Display
export const SwipeAnalyticsContainer = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem;
  color: white;
  font-size: 0.875rem;

  @media (max-width: 768px) {
    display: none; /* Hide on mobile for better UX */
  }
`;

export const SwipeAnalyticsItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SwipeAnalyticsLabel = styled.span`
  opacity: 0.8;
`;

export const SwipeAnalyticsValue = styled.span`
  font-weight: 600;
`;

// Settings Panel
export const SwipeSettingsContainer = styled.div`
  position: absolute;
  top: 2rem;
  left: 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem;
  color: white;
  min-width: 200px;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: 1rem;
    min-width: 180px;
    padding: 0.75rem;
  }

  @media (max-width: 480px) {
    min-width: 160px;
    padding: 0.5rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 360px) {
    min-width: 140px;
    padding: 0.375rem;
    margin-bottom: 0.5rem;
  }
`;

export const SwipeSettingsTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

export const SwipeSettingsItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SwipeSettingsLabel = styled.span`
  font-size: 0.875rem;
  opacity: 0.8;
`;

export const SwipeSettingsToggle = styled.input`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  outline: none;
  cursor: pointer;
  position: relative;
  appearance: none;

  &:checked {
    background: #667eea;
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    transition: transform 0.2s ease;
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

// Gesture Hints
export const SwipeGestureHints = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2rem;
  color: white;
  font-size: 0.875rem;
  opacity: 0.8;

  @media (max-width: 768px) {
    bottom: 1.5rem;
    gap: 1.5rem;
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    bottom: 1rem;
    gap: 1rem;
    font-size: 0.625rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  @media (max-width: 360px) {
    bottom: 0.75rem;
    gap: 0.75rem;
    font-size: 0.5rem;
  }
`;

export const SwipeGestureHint = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SwipeGestureIcon = styled.span`
  font-size: 1rem;
`;

// Progress Indicator
export const SwipeProgressContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;

  @media (max-width: 768px) {
    top: 0.75rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    border-radius: 12px;
  }

  @media (max-width: 360px) {
    top: 0.375rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.5rem;
    border-radius: 8px;
  }
`;

// Super Like Counter
export const SuperLikeCounter = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(102, 126, 234, 0.9);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.75rem;
    border-radius: 16px;
    gap: 0.375rem;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    border-radius: 12px;
    gap: 0.25rem;
  }

  @media (max-width: 360px) {
    top: 0.375rem;
    right: 0.375rem;
    padding: 0.125rem 0.375rem;
    font-size: 0.5rem;
    border-radius: 8px;
    gap: 0.125rem;
  }
`;

export const SuperLikeIcon = styled.span`
  font-size: 1rem;
`;

// Undo Timer
export const UndoTimerContainer = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const UndoTimerIcon = styled.span`
  font-size: 1rem;
`;

export const UndoTimerText = styled.span`
  font-size: 0.875rem;
`;

// Utility classes
export const CardInfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const CardInfoRowInline = styled.div`
  display: flex;
  align-items: center;
`;

// Swipe Interface specific styles
export const SwipeHeaderButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const SwipeSettingsToggleButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const SwipeHeaderContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1000;

  @media (max-width: 768px) {
    top: 0.75rem;
    left: 0.75rem;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    left: 0.5rem;
  }

  @media (max-width: 360px) {
    top: 0.375rem;
    left: 0.375rem;
  }
`;

export const SwipeSettingsToggleContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1000;

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    right: 0.5rem;
  }

  @media (max-width: 360px) {
    top: 0.375rem;
    right: 0.375rem;
  }
`;

export const SwipeDebugInfo = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  z-index: 9999;
`;

// Swipe Verdict Stamps
// A drag has to say what it will do BEFORE the card leaves the screen,
// otherwise left and right are a coin flip the player only resolves after
// they have already committed. These fade in with drag distance, so reaching
// full opacity is also the signal that the gesture has passed the threshold.
export const SwipeVerdictStamp = styled(motion.div)<{ $variant: 'keep' | 'pass' }>`
  position: absolute;
  top: 24px;
  ${props => (props.$variant === 'keep' ? 'left: 24px;' : 'right: 24px;')}
  z-index: 30;
  pointer-events: none;
  padding: 0.375rem 1rem;
  border-radius: 12px;
  border: 4px solid ${props => (props.$variant === 'keep' ? '#20bf6b' : '#ee5a52')};
  color: ${props => (props.$variant === 'keep' ? '#20bf6b' : '#ee5a52')};
  background: rgba(255, 255, 255, 0.88);
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transform: rotate(${props => (props.$variant === 'keep' ? '-14deg' : '14deg')});

  @media (max-width: 480px) {
    top: 16px;
    ${props => (props.$variant === 'keep' ? 'left: 16px;' : 'right: 16px;')}
    padding: 0.25rem 0.75rem;
    border-width: 3px;
    font-size: 1.25rem;
  }
`;
